import { Injectable } from '@angular/core';
import { clearStudentPersonalDetails, DigitalAccessName, setUnsettedValuesInStudent, Student, studentSorter, UnlimitedDigitalAccesses } from '../classes/model/student';
import { BackendService } from './common/backend.service';
import { FilloutDetails } from '../classes/model/fillout-details';
import { HttpErrorHandlerService } from './common/http-error-handler.service';
import { HttpResponseData } from '../classes/http-communication';
import { DataCacherService } from './data-cacher-service';
import { Permission, PermissionString } from '../classes/model/permissions';
import { NotifierService } from 'src/app/services/common/notifier-service.service';
import { StudentPaginationParams, PaginatedStudentsResponse, PaginatedStudentsApiResponse } from '../classes/model/student-pagination';

@Injectable({
  providedIn: 'root'
})
export class StudentService implements DataCacherService {
  readonly studentsApiUrlFragment = "/api/admin/user";
  readonly filloutDetailsApiUrlFragment = "/api/admin/history";
  readonly createGiftApiUrlFragment = "/api/admin/user/add_explicit_gift_to_student"
  readonly deleteStudentPersinalDetailsApiUrlFragment = "/api/admin/user/delete_user"

  private students:Array<Student>;

  private readonly requiredPermissionsForDataLoading:ReadonlyArray<PermissionString> = [ Permission.StudentsRead ];

  constructor(
    private backendService:BackendService,
    private httpErrorHandlerService:HttpErrorHandlerService,
    private notifierService:NotifierService
  ) {
    this.students = new Array<Student>();
  }

  public getStudentsArrayRef():ReadonlyArray<Readonly<Student>> {
    return this.students;
  }

  public async fetchStudents():Promise<Array<Readonly<Student>>> {
    let students:Array<Readonly<Student>> = [];

    try {
      const response:HttpResponseData<Array<Readonly<Student>>> = await this.backendService.callApi(this.studentsApiUrlFragment, "GET", undefined,undefined,undefined,200000);
      students = response.data;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a tanulók lekérdezése közben.");
    }

    return students;
  }

  public async fetchStudentsPaginated(params: StudentPaginationParams): Promise<PaginatedStudentsResponse> {
    const apiUrl = this.studentsApiUrlFragment;
    let queryParams: string[] = [];

    // Build query parameters
    if (params.page !== undefined) {
      queryParams.push(`page=${params.page}`);
    }
    if (params.limit !== undefined) {
      queryParams.push(`limit=${params.limit}`);
    }
    if (params.sortOrder !== undefined) {
      queryParams.push(`sortOrder=${params.sortOrder}`);
    }
    if (params.uuid !== undefined && params.uuid.trim() !== '') {
      queryParams.push(`uuid=${encodeURIComponent(params.uuid)}`);
    }
    if (params.nameContains !== undefined && params.nameContains.trim() !== '') {
      queryParams.push(`nameContains=${encodeURIComponent(params.nameContains)}`);
    }
    if (params.emailContains !== undefined && params.emailContains.trim() !== '') {
      queryParams.push(`emailContains=${encodeURIComponent(params.emailContains)}`);
    }
    if (params.customerIdExact !== undefined && params.customerIdExact.trim() !== '') {
      queryParams.push(`customerIdExact=${encodeURIComponent(params.customerIdExact)}`);
    }

    const fullUrl = queryParams.length > 0 ? `${apiUrl}?${queryParams.join('&')}` : apiUrl;

    try {
      const response = await this.backendService.callApi<undefined, PaginatedStudentsApiResponse>(
        fullUrl,
        "GET",
        undefined,
        undefined,
        undefined,
        200000
      );

      // Transform API response to internal format
      const students: Student[] = response.data.items.map(apiStudent => {
        const student: Student = {
          uuid: apiStudent.uuid,
          fullname: (apiStudent.firstName && apiStudent.lastName)
            ? `${apiStudent.firstName} ${apiStudent.lastName}`
            : (apiStudent.firstName || apiStudent.lastName || ''),
          email: apiStudent.email,
          customerId: apiStudent.customerId,
          registrationDate: apiStudent.registrationDate,
          // Set other required fields with defaults
          firstName: apiStudent.firstName || '',
          lastName: apiStudent.lastName || '',
          profileImageUrl: apiStudent.profileImageUrl || '',
          // Add any other required Student fields with appropriate defaults
        } as Student;

        return setUnsettedValuesInStudent(student);
      });

      return {
        items: students,
        totalItems: response.data.totalItems,
        totalPages: response.data.totalPages,
        currentPage: response.data.currentPage,
        itemsPerPage: response.data.itemsPerPage
      };
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a tanulók lekérdezése közben.");
      // Return empty response on error
      return {
        items: [],
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 20
      };
    }
  }

  public async getStudent(studentUuid:string):Promise<Student|null> {
    const apiUrlFragment:string = this.studentsApiUrlFragment + "/" + studentUuid;
    let result:Student|null = null;

    try {
      const response:HttpResponseData<Student> = await this.backendService.callApi(apiUrlFragment, "GET");
      result = setUnsettedValuesInStudent(response.data);
    } catch(error:any) {
      if(error?.error?.error === "USER_NOT_FOUND") {
        this.notifierService.notify("error", "A keresett tanuló nem található.");
        throw error;
      } else {
        this.httpErrorHandlerService.handleError(error, "Hiba a tanuló lekérdezése közben.");
      }
    }

    return result;
  }

  async getFillout(filloutUuid:string):Promise<{ details:FilloutDetails }> {
    const apiUrlFragment:string = this.filloutDetailsApiUrlFragment + "/" + filloutUuid;
    let result:{ details:FilloutDetails };

    try {
      const response:HttpResponseData<{ details:FilloutDetails }> = await this.backendService.callApi(apiUrlFragment, "GET");
      result = response.data;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a kitöltés részletek lekérdezése közben.");
    }

    return result;
  }

  public async createGiftForStudent(student:Student, packageDefinitionUuid:string, reason:string, message:string):Promise<any> {
    const httpBody:Object = {
      data: {
        studentUuid: student.uuid,
        packageDefinitionUuid: packageDefinitionUuid,
        reason: reason,
        message: message
      }
    };

    try {
      const response:{ createdNewPackage:any } = await this.backendService.callApi(this.createGiftApiUrlFragment, "POST", httpBody);
      student.pendingExplicitGiftedPackage.push(response.createdNewPackage);
      return response.createdNewPackage;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba az ajándék létrehozása közben.");
    }
  }

  /**
   * Korlátlan (lejárat nélküli) hozzáférés adása vagy elvétele. Siker esetén a tanuló
   * unlimitedDigitalAccesses mezője a szerver által visszaadott állapotra frissül.
   *
   * @returns true, ha sikeres volt a módosítás
   */
  public async setUnlimitedDigitalAccess(student:Student, accessName:DigitalAccessName, isUnlimited:boolean):Promise<boolean> {
    const apiUrlFragment:string = `${this.studentsApiUrlFragment}/${student.uuid}/unlimited-digital-accesses/${accessName}`;

    try {
      const response:HttpResponseData<{ unlimitedDigitalAccesses:UnlimitedDigitalAccesses }> = await this.backendService.callApi(apiUrlFragment, isUnlimited ? "PUT" : "DELETE");
      student.unlimitedDigitalAccesses = response.data.unlimitedDigitalAccesses;
      return true;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a korlátlan hozzáférés módosítása közben.");
      return false;
    }
  }

  public async deleteStudentPersonalDetails(student:Student):Promise<void> {
    const httpBody:Object = {
      data: {
        userUuid: student.uuid
      }
    };

    try {
      await this.backendService.callApi(this.deleteStudentPersinalDetailsApiUrlFragment, "POST", httpBody);
      clearStudentPersonalDetails(student);
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a tanuló személyes adatainak törlése közben.");
    }
  }

  public async loadDataIntoCache():Promise<void> {
    const students:Array<Readonly<Student>> = await this.fetchStudents();
    this.students.replaceArrayElements(students);
    this.students.forEach(setUnsettedValuesInStudent);
    this.students.sort(studentSorter);
  }

  public clearCachedData():void {
    this.students.length = 0;
  }

  public getRequiredPermissionsForDataLoading(): ReadonlyArray<PermissionString> {
    return this.requiredPermissionsForDataLoading;
  }

  public getNameOfCachedData(): string {
    return "Monitorozott felhasználók";
  }

}
